import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import api, { resolveMedia } from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Reveal, MaskedLine, EASE } from "@/components/motion/Reveal";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Tutti");
  useSEO({ title: "Diario", description: "Il diario di Giovanna Matino: tendenze arredo, ristrutturare, stili, colore e materia." });

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([])).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(posts.map((p) => p.category || "Diario"))).filter(Boolean);
    return ["Tutti", ...cats];
  }, [posts]);

  const filtered = filter === "Tutti" ? posts : posts.filter((p) => (p.category || "Diario") === filter);
  const [hero, ...rest] = filtered;

  return (
    <div data-testid="page-blog">
      <section className="container-x pt-10 pb-10">
        <motion.span className="eyebrow block" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>Diario</motion.span>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6 max-w-4xl">
          <MaskedLine delay={0.15}>Appunti di</MaskedLine>
          <MaskedLine delay={0.32}><em className="text-gold font-light">progettazione.</em></MaskedLine>
        </h1>
        <motion.p className="mt-8 text-taupe max-w-2xl text-lg font-light leading-relaxed" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease: EASE }}>
          Tendenze arredo, guide alla ristrutturazione, stili e ispirazioni. Un archivio pensato per chi vuole scelte consapevoli.
        </motion.p>
      </section>

      <section className="container-x mb-10">
        <div className="flex flex-wrap gap-2 border-y border-hair py-4">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 text-xs tracking-[0.14em] uppercase transition-colors ${
                filter === c ? "bg-ink text-bone" : "text-ink/70 hover:text-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="container-x pb-24">
        {loading ? (
          <div className="text-taupe text-center py-24">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="text-taupe text-center py-24">Nessun articolo per questa categoria.</div>
        ) : (
          <>
            {/* Magazine hero — big featured article */}
            {hero && (
              <Reveal>
              <Link to={`/blog/${hero.slug}`} className="group block mb-16 border-b border-hair pb-12" data-testid={`blog-hero-${hero.slug}`}>
                <div className="grid lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-7 overflow-hidden">
                    <img src={resolveMedia(hero.cover_image)} alt={hero.title} className="w-full h-[380px] md:h-[520px] object-cover transition-transform duration-700 group-hover:scale-[1.02]" />
                  </div>
                  <div className="lg:col-span-5">
                    <span className="eyebrow text-gold">{hero.category || "Diario"}</span>
                    <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-[1.1] group-hover:text-gold transition-colors">{hero.title}</h2>
                    {hero.excerpt && <p className="mt-6 text-taupe leading-relaxed">{hero.excerpt}</p>}
                    <div className="mt-6 text-xs text-taupe">
                      di {hero.author} · {new Date(hero.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                    <div className="mt-8 inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase link-underline">
                      Leggi l'articolo <ArrowUpRight size={16} />
                    </div>
                  </div>
                </div>
              </Link>
              </Reveal>
            )}

            {/* Grid of remaining */}
            {rest.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14 items-start" data-testid="blog-grid">
                {rest.map((p, i) => (
                  <Reveal key={p.id} delay={(i % 3) * 0.1} className={`h-full ${i % 3 === 1 ? "lg:mt-16" : ""}`}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="group flex flex-col h-full text-left"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <div className="overflow-hidden">
                      <img src={resolveMedia(p.cover_image)} alt={p.title} className="w-full h-[320px] object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="mt-5 flex flex-col flex-1">
                      <span className="text-[10px] tracking-[0.18em] uppercase text-gold">{p.category || "Diario"}</span>
                      <h3 className="font-serif text-2xl mt-2 leading-tight group-hover:text-gold transition-colors text-pretty">{p.title}</h3>
                      {p.excerpt && <p className="mt-3 text-sm text-taupe leading-relaxed line-clamp-2">{p.excerpt}</p>}
                      <div className="mt-auto pt-4 text-xs tracking-[0.14em] uppercase text-ink group-hover:text-gold transition-colors">Leggi →</div>
                    </div>
                  </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
